import axios from 'axios'
import { notifications } from '@mantine/notifications'
import { isJWTFresh } from '@common/utils/validation/authValidators'
import {
  AlertCircle,
  CheckCircle,
  Clock,
  LogOut,
} from 'lucide-react'
import backend, {
  clearAuthSession,
  getStoredAccessToken,
  getStoredRefreshToken,
  getStoredTokenType,
  persistAuthSession,
} from '@api/backend'
import { AxiosRequestConfigCustom } from '@projtypes/request'
import { AuthTokenResponse } from '@projtypes/token'
import React from 'react'

// These error codes from the token endpoint mean the session is unrecoverable —
// the refresh token is either expired or invalid (e.g. stale signature, revoked).
const SESSION_CLEARING_ERROR_CODES = new Set([
  80004, // Refresh Token expired
  80099, // Invalid token (revoked / signature mismatch)
])

const isSessionInvalidError = (error: any) => {
  const code = error?.response?.data?.error_code
  return SESSION_CLEARING_ERROR_CODES.has(code) ||
    error?.response?.data?.message === 'Refresh Token expired.'
}

interface AuthCallbacks {
  applyToken: (token: AuthTokenResponse) => void
  clearSession: () => void
  setError: (value: boolean) => void
}

const refreshConfig: AxiosRequestConfigCustom = {
  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  _skipAuthRefresh: true,
}

export const loginWithPassword = async (
  email: string,
  password: string,
  { applyToken, setError }: Pick<AuthCallbacks, 'applyToken' | 'setError'>,
): Promise<void> => {
  // Logout notifications use autoClose: false and would otherwise sit on the
  // login screen forever; a new login attempt supersedes them.
  notifications.clean()

  try {
    const resp = await backend.post(
      '/users/login/token/',
      new URLSearchParams({
        username: email,
        password: password,
        grant_type: 'password',
      }),
      refreshConfig,
    )

    const token: AuthTokenResponse = {
      access_token: resp.data.access_token,
      refresh_token: resp.data.refresh_token ?? null,
      token_type: resp.data.token_type,
    }

    persistAuthSession(token)
    applyToken(token)
  } catch (error: any) {
    console.log(error)
    setError(true)
    if (axios.isAxiosError(error)) {
      const data = error.response?.data
      const errorCode = data?.error_code
      const baseMessage =
        data?.detail ||
        data?.message ||
        'An error occurred while trying to login. Please try again.'
      notifications.show({
        title: 'Login Failed',
        message: errorCode ? `${baseMessage} (code: ${errorCode})` : baseMessage,
        color: 'red',
        icon: <AlertCircle size={16} />,
        autoClose: false,
      })
    }
  }
}

export const requestPasswordResetEmail = async (
  email: string,
): Promise<void> => {
  try {
    await backend.post('/users/me/forgot_password', { email })
    notifications.show({
      title: 'Email Sent',
      message: 'Email sent successfully',
      color: 'teal',
      icon: <CheckCircle size={16} />,
    })
  } catch (error: any) {
    console.log(error)
    notifications.show({
      title: 'Request Failed',
      message:
        'An Error occurred while requesting your password reset email. Please try again...',
      color: 'red',
      icon: <AlertCircle size={16} />,
      autoClose: false,
    })
  }
}

interface ResetPasswordParams {
  newPassword: string
  verifyPassword: string
  token: string
}

export const resetPasswordWithToken = async ({
  newPassword,
  verifyPassword,
  token,
}: ResetPasswordParams): Promise<void> => {
  try {
    await backend.post(`/users/me/reset_password/${token}`, {
      newPassword,
      verifyPassword,
    })
    notifications.show({
      title: 'Password Reset',
      message: 'Password reset successfully. Please sign in with your new password.',
      color: 'teal',
      icon: <CheckCircle size={16} />,
    })
  } catch (error: any) {
    console.log(error)
    const errorCode = error?.response?.data?.error_code
    const message = errorCode === 80004
      ? 'Reset password link had expired, please request a new link from the forgot password page'
      : 'An Error occurred while resetting your password. Please try again...'
    notifications.show({
      title: 'Reset Failed',
      message,
      color: 'red',
      icon: <AlertCircle size={16} />,
      autoClose: false,
    })
    throw error
  }
}

interface RegisterUserParams {
  firstname: string
  lastname: string
  mobile: string
  newPassword: string
  newPasswordConfirmation: string
  token: string
  uom: string
}

export const registerUserWithToken = async ({
                                             firstname,
                                             lastname,
                                             mobile,
                                             newPassword,
                                             newPasswordConfirmation,
                                             token,
                                             uom,
                                           }: RegisterUserParams): Promise<void> => {
  try {
    await backend.post(`/users/register/${token}`, {
      firstName: firstname,
      lastName: lastname,
      mobile,
      password: newPassword,
      verifyPassword: newPasswordConfirmation,
      userPreferences: { uom },
    })
    notifications.show({
      title: 'Account Created',
      message: 'Account created successfully',
      color: 'teal',
      icon: <CheckCircle size={16} />,
    })
  } catch (error: any) {
    console.log(error)
    const errorCode = error?.response?.data?.error_code
    const apiMessage = error?.response?.data?.message
    notifications.show({
      title: 'Account Creation Failed',
      message: apiMessage
        ? `${apiMessage}, Error code: ${errorCode}`
        : `An Error occurred while registering your account, Error code: ${errorCode}. Please try again...`,
      color: 'red',
      icon: <AlertCircle size={16} />,
      autoClose: false,
    })
    throw error
  }
}

const performTokenRefresh = async (
  refreshToken: string,
  applyToken: AuthCallbacks['applyToken'],
): Promise<AuthTokenResponse> => {
  const resp = await backend.post(
    '/users/login/token/',
    new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    }),
    refreshConfig,
  )

  const token: AuthTokenResponse = {
    access_token: resp.data.access_token,
    refresh_token: resp.data.refresh_token ?? null,
    token_type: resp.data.token_type,
  }

  persistAuthSession(token)
  applyToken(token)

  return token
}

export const refreshWithToken = async (
  { applyToken, clearSession, setError }: AuthCallbacks,
): Promise<AuthTokenResponse | null> => {
  const refreshToken = getStoredRefreshToken()

  if (!refreshToken) {
    clearAuthSession()
    clearSession()
    return null
  }

  try {
    return await performTokenRefresh(refreshToken, applyToken)
  } catch (error: any) {
    console.log(error)

    if (!isSessionInvalidError(error)) {
      setError(true)
      throw error
    }

    // Refresh tokens rotate on every use, so a session-invalid error can mean
    // another window already refreshed and persisted the new token. Retry with
    // the rotated token instead of wiping that window's live session.
    const rotatedRefreshToken = getStoredRefreshToken()

    if (rotatedRefreshToken && rotatedRefreshToken !== refreshToken) {
      try {
        return await performTokenRefresh(rotatedRefreshToken, applyToken)
      } catch (retryError: any) {
        console.log(retryError)

        if (!isSessionInvalidError(retryError)) {
          setError(true)
          throw retryError
        }
      }
    }

    clearAuthSession()
    clearSession()
    throw error
  }
}

export type LogoutReason = 'manual' | 'idle' | 'password-change'

const logoutNotifications: Record<LogoutReason, { title: string; message: string; icon: React.ReactElement }> = {
  'manual': {
    title: 'Logged Out',
    message: 'You have been successfully logged out.',
    icon: <LogOut size={16} />,
  },
  'idle': {
    title: 'Session Expired',
    message: 'You were logged out due to inactivity.',
    icon: <Clock size={16} />,
  },
  'password-change': {
    title: 'Password Updated',
    message: 'Please sign in with your new password.',
    icon: <CheckCircle size={16} />,
  },
}

export const logoutSession = async (
  { clearSession }: Pick<AuthCallbacks, 'clearSession'>,
  reason: LogoutReason = 'manual',
): Promise<void> => {
  clearAuthSession()
  clearSession()
  const { title, message, icon } = logoutNotifications[reason]
  notifications.show({
    title,
    message,
    color: 'teal',
    icon,
    autoClose: reason === 'manual' ? 5000 : false,
  })
}

export const bootstrapSession = async (
  callbacks: AuthCallbacks,
): Promise<AuthTokenResponse | null> => {
  // Snapshot the AT before attempting refresh — refreshWithToken may call
  // clearAuthSession() on session-invalid errors (80004, 80099), which would
  // also remove a still-valid access token before we can fall back to it.
  const savedAccessToken = getStoredAccessToken()
  const refreshToken = getStoredRefreshToken()

  if (refreshToken) {
    try {
      return await refreshWithToken(callbacks)
    } catch {
      // Refresh failed — fall through to access-token restore, matching the
      // original bootstrapAuthAction behavior.
    }
  }

  const accessToken = savedAccessToken ?? getStoredAccessToken()

  if (!accessToken || !isJWTFresh(accessToken)) {
    clearAuthSession()
    callbacks.clearSession()
    return null
  }

  const token: AuthTokenResponse = {
    access_token: accessToken,
    refresh_token: null,
    token_type: getStoredTokenType(),
  }

  try {
    callbacks.applyToken(token)
  } catch {
    // Stored access token is not a decodeable JWT — clear everything cleanly
    clearAuthSession()
    callbacks.clearSession()
    return null
  }

  return token
}
